import { useCallback, useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { logger, LOG_CONTEXTS } from '@/utils/logger';
import { cn } from '@/lib/utils';

import { ChannelTable } from './ChannelTable';
import { ChannelFilters } from './ChannelFilters';
import { ChannelBulkActions } from './ChannelBulkActions';
import { useChannelStore } from '@/stores/channelStore';
import { Button } from '@/components/ui/button';
import {
  Plus,
  Settings,
  RefreshCw,
  ArrowUpDown,
  Zap,
  Loader2
} from 'lucide-react';
import type { Channel } from '../types';
import { getPlanId } from '../types';
import { ChannelModals } from './ChannelModals';
import { toast } from 'sonner';
import { BulkImportButton } from './import/BulkImportButton';
import { ChannelTypeModal } from './add-channel-types/ChannelTypeModal';
import { usePlans } from '@/hooks/usePlans';
import type { VersionAwareProps } from '@/features/versions/types/props';
import { ErrorDisplay } from '@/components/common/ErrorBoundary';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Component instance counter to track multiple instances
let instanceCounter = 0;

// Track component renders
const COMPONENT_NAME = 'ChannelTab';

interface ChannelFilters {
  search: string;
  categoryId: number | undefined;
  planId: number | undefined;
  isLocal: boolean | undefined;
  isDuplicate: boolean | undefined;
}

// Add sort options type - NEW
type SortField = 'name' | 'number' | null;
type SortDirection = 'asc' | 'desc';

interface ChannelNumberRow {
  id: number;
  number: string;
  is_sequence: boolean;
  type: {
    id: number;
    name: string;
    display_order: number;
  };
  channel: {
    id: number;
    name: string;
    description?: string;
    category_id: number | null;
    provider_id: number;
    version_id: number;
    category?: {
      id: number;
      name: string;
    };
    categories?: Array<{
      category_id: number;
      is_default: boolean;
      category?: {
        id: number;
        name: string;
      };
    }>;
    plans: Array<{
      plan: {
        id: number;
        name: string;
        color_index: number;
      };
    }>;
    price?: number | null;
    is_local: boolean;
    is_duplicate: boolean;
  };
}

export function ChannelTab({ providerId, versionId, regionId, isReadOnly }: VersionAwareProps) {
  const navigate = useNavigate();
  
  // Create a unique instance ID for this component instance
  const [instanceId] = useState(() => {
    instanceCounter++;
    return `${COMPONENT_NAME}_${instanceCounter}`;
  });
  
  // Log component mount
  useEffect(() => {
    logger.info(LOG_CONTEXTS.RENDER, `Component mounted: ${instanceId}`, {
      providerId,
      versionId,
      regionId,
      isReadOnly
    });
    
    return () => {
      logger.info(LOG_CONTEXTS.RENDER, `Component unmounted: ${instanceId}`);
    };
  }, [instanceId, providerId, versionId, regionId, isReadOnly]);
  
  const { selectedChannels, openCreateModal: originalOpenCreateModal } = useChannelStore();
  const [filters, setFilters] = useState<ChannelFilters>({
    search: '',
    categoryId: undefined,
    planId: undefined,
    isLocal: undefined,
    isDuplicate: undefined
  });

  // Add sorting state
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [_loading, setLoading] = useState(false);

  // Fetch plans data for bulk plan assigner
  const { data: plans } = usePlans(providerId, versionId);

  // Log filter changes
  useEffect(() => {
    logger.debug(LOG_CONTEXTS.CHANNELS, `Filters changed in ${instanceId}`, { filters });
  }, [filters, instanceId]);

  // Define query key as a tuple
  const queryKey = useMemo(() =>
    ['channels', providerId, versionId, regionId, filters.search, filters.categoryId, filters.planId, filters.isLocal, filters.isDuplicate, sortField, sortDirection] as const,
    [providerId, versionId, regionId, filters.search, filters.categoryId, filters.planId, filters.isLocal, filters.isDuplicate, sortField, sortDirection]
  );

  // Toggle sort for a field
  const toggleSort = useCallback((field: SortField) => {
    logger.debug(
      LOG_CONTEXTS.CHANNELS,
      `Toggling sort in ${instanceId}`,
      { currentField: sortField, newField: field, currentDirection: sortDirection }
    );

    if (sortField === field) {
      // If already sorting by this field, toggle direction
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // If sorting by a new field, set it and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection, instanceId]);
  
  // The fetchChannels function fetches all channels with filtering support
  const fetchChannels = async (): Promise<Channel[]> => {
    setLoading(true);
    const queryRequestId = logger.createRequestId();

    try {
      logger.debug(
        LOG_CONTEXTS.QUERY,
        `Executing query in ${instanceId}`,
        { filters, sortField, sortDirection },
        queryRequestId
      );

      let channelIds: number[] = [];

      if (filters.categoryId) {
        // When filtering by category, we need to find channels that belong to this category
        // either through primary category_id OR through channel_categories junction table

        // Get channels with primary category_id matching the filter
        const primaryCategoryQuery = supabase
          .from('channels')
          .select('id')
          .eq('provider_id', providerId)
          .eq('version_id', versionId)
          .eq('category_id', filters.categoryId);

        if (filters.isLocal !== undefined) {
          primaryCategoryQuery.eq('is_local', filters.isLocal);
        }

        if (filters.isDuplicate !== undefined) {
          primaryCategoryQuery.eq('is_duplicate', filters.isDuplicate);
        }

        const { data: primaryCategoryChannels } = await primaryCategoryQuery;

        // Get channels through channel_categories junction table
        const junctionCategoryQuery = supabase
          .from('channel_categories')
          .select(`
            channel:channels!inner(id, provider_id, version_id, is_local)
          `)
          .eq('category_id', filters.categoryId)
          .eq('channel.provider_id', providerId)
          .eq('channel.version_id', versionId);

        if (filters.isLocal !== undefined) {
          junctionCategoryQuery.eq('channel.is_local', filters.isLocal);
        }

        if (filters.isDuplicate !== undefined) {
          junctionCategoryQuery.eq('channel.is_duplicate', filters.isDuplicate);
        }

        const { data: junctionCategoryChannels } = await junctionCategoryQuery;

        // Combine both sets of channel IDs and remove duplicates
        const primaryIds = primaryCategoryChannels?.map(c => c.id) || [];
        const junctionIds = junctionCategoryChannels?.map(c => c.channel.id) || [];
        channelIds = [...new Set([...primaryIds, ...junctionIds])];

      } else {
        // No category filter, get all channels for this provider/version
        const allChannelsQuery = supabase
          .from('channels')
          .select('id')
          .eq('provider_id', providerId)
          .eq('version_id', versionId);

        if (filters.isLocal !== undefined) {
          allChannelsQuery.eq('is_local', filters.isLocal);
        }

        if (filters.isDuplicate !== undefined) {
          allChannelsQuery.eq('is_duplicate', filters.isDuplicate);
        }

        const { data: allChannels } = await allChannelsQuery;
        channelIds = allChannels?.map(c => c.id) || [];
      }

      // Sort channel IDs
      channelIds.sort((a, b) => a - b);

      if (channelIds.length === 0) {
        setLoading(false);
        return [];
      }

      logger.debug(
        LOG_CONTEXTS.QUERY,
        `Fetching ${channelIds.length} channels with optimized two-query approach in ${instanceId}`,
        { channelCount: channelIds.length },
        queryRequestId
      );

      // OPTIMIZED APPROACH: Split into two queries to avoid cartesian product timeout
      // Query 1: Fetch channels with categories and plans (no channel_numbers join)
      const channelsQuery = supabase
        .from('channels')
        .select(`
          *,
          category:categories(id, name),
          categories:channel_categories(
            category_id,
            is_default,
            category:categories(id, name)
          ),
          plans:channel_plans(
            plan:plans(id, name, color_index, version_id)
          )
        `)
        .in('id', channelIds);

      // Apply sorting on channels if needed
      if (sortField === 'name') {
        channelsQuery.order('name', { ascending: sortDirection === 'asc' });
      }

      const { data: channelsData, error: channelsError } = await channelsQuery;

      if (channelsError) {
        logger.error(
          LOG_CONTEXTS.QUERY,
          `Channels query error in ${instanceId}`,
          { error: channelsError },
          queryRequestId
        );
        throw channelsError;
      }

      logger.debug(
        LOG_CONTEXTS.QUERY,
        `Fetched ${channelsData?.length || 0} channels in ${instanceId}`,
        { channelCount: channelsData?.length },
        queryRequestId
      );

      // Query 2: Fetch channel numbers separately (much faster!)
      const numbersQuery = supabase
        .from('channel_numbers')
        .select(`
          id,
          channel_id,
          number,
          is_sequence,
          type:channel_types(id, name, display_order)
        `)
        .in('channel_id', channelIds);

      // Apply sorting on numbers if needed
      if (sortField === 'number') {
        numbersQuery.order('number', { ascending: sortDirection === 'asc' });
      }

      const { data: numbersData, error: numbersError } = await numbersQuery;

      if (numbersError) {
        logger.error(
          LOG_CONTEXTS.QUERY,
          `Channel numbers query error in ${instanceId}`,
          { error: numbersError },
          queryRequestId
        );
        throw numbersError;
      }

      logger.debug(
        LOG_CONTEXTS.QUERY,
        `Fetched ${numbersData?.length || 0} channel numbers in ${instanceId}`,
        { numberCount: numbersData?.length },
        queryRequestId
      );

      // Merge the data: Create a map of numbers grouped by channel_id
      const numbersMap = new Map<number, typeof numbersData>();
      if (numbersData) {
        numbersData.forEach(num => {
          // Skip numbers without a channel_id (shouldn't happen but be safe)
          if (num.channel_id === null) return;

          if (!numbersMap.has(num.channel_id)) {
            numbersMap.set(num.channel_id, []);
          }
          numbersMap.get(num.channel_id)!.push(num);
        });
      }

      // Transform channels data to match ChannelNumberRow format for processChannelData
      const transformedData: ChannelNumberRow[] = [];

      if (channelsData) {
        channelsData.forEach(channel => {
          const channelNumbers = numbersMap.get(channel.id) || [];

          // If channel has numbers, create a row for each number
          if (channelNumbers.length > 0) {
            channelNumbers.forEach(num => {
              transformedData.push({
                id: num.id,
                number: num.number,
                is_sequence: num.is_sequence || false,
                type: num.type as { id: number; name: string; display_order: number },
                channel: {
                  id: channel.id,
                  name: channel.name,
                  description: channel.description ?? undefined,
                  category_id: channel.category_id,
                  provider_id: channel.provider_id ?? providerId,
                  version_id: channel.version_id ?? versionId,
                  category: channel.category ?? undefined,
                  categories: (channel.categories || []).map(c => ({
                    category_id: c.category_id,
                    is_default: c.is_default,
                    category: c.category ?? undefined
                  })),
                  plans: (channel.plans || [])
                    .filter(p => p.plan !== null)
                    .map(p => ({
                      plan: {
                        id: p.plan!.id,
                        name: p.plan!.name,
                        color_index: p.plan!.color_index ?? 0
                      }
                    })),
                  price: channel.price ?? undefined,
                  is_local: channel.is_local || false,
                  is_duplicate: channel.is_duplicate || false
                }
              });
            });
          } else {
            // Channel without numbers - create a single entry with default number
            transformedData.push({
              id: 0,
              number: '*',
              is_sequence: false,
              type: { id: 0, name: 'Default', display_order: 0 },
              channel: {
                id: channel.id,
                name: channel.name,
                description: channel.description ?? undefined,
                category_id: channel.category_id,
                provider_id: channel.provider_id ?? providerId,
                version_id: channel.version_id ?? versionId,
                category: channel.category ?? undefined,
                categories: (channel.categories || []).map(c => ({
                  category_id: c.category_id,
                  is_default: c.is_default,
                  category: c.category ?? undefined
                })),
                plans: (channel.plans || [])
                  .filter(p => p.plan !== null)
                  .map(p => ({
                    plan: {
                      id: p.plan!.id,
                      name: p.plan!.name,
                      color_index: p.plan!.color_index ?? 0
                    }
                  })),
                price: channel.price ?? undefined,
                is_local: channel.is_local || false,
                is_duplicate: channel.is_duplicate || false
              }
            });
          }
        });
      }

      logger.debug(
        LOG_CONTEXTS.QUERY,
        `Transformed ${transformedData.length} rows for processing in ${instanceId}`,
        { rowCount: transformedData.length },
        queryRequestId
      );

      // Process data to get channels (existing function)
      let channels = processChannelData(transformedData);

      // Apply search filter
      if (filters.search && filters.search.trim() !== '') {
        const searchTerm = filters.search.trim().toLowerCase();
        channels = channels.filter(channel =>
          channel.name.toLowerCase().includes(searchTerm) ||
          channel.numbers.some(num => num.number.toLowerCase().includes(searchTerm))
        );
      }

      // Apply plan filter
      if (filters.planId) {
        channels = channels.filter(channel =>
          channel.plans?.some(p => getPlanId(p) === filters.planId) || false
        );
      }

      setLoading(false);
      return channels;
    } catch (error) {
      handleQueryError(error);
      setLoading(false);
      return [];
    }
  };

  const { data: channels, error, isFetching } = useQuery<Channel[], Error>({
    queryKey,
    queryFn: fetchChannels,
    // Increase staleTime to prevent frequent refetching
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Keep data in cache longer
    gcTime: 10 * 60 * 1000, // 10 minutes
    // Prevent refetching on window focus
    refetchOnWindowFocus: false,
  });

  // Log when channels data changes
  useEffect(() => {
    logger.debug(
      LOG_CONTEXTS.CHANNELS,
      `Channels data updated in ${instanceId}`,
      { channelCount: channels?.length }
    );
  }, [channels, instanceId]);

  // DEBUG: Log isFetching changes
  useEffect(() => {
    console.log('[DEBUG] ChannelTab isFetching changed:', isFetching);
  }, [isFetching]);

  const handleFiltersChange = useCallback((newFilters: ChannelFilters) => {
    logger.debug(
      LOG_CONTEXTS.CHANNELS,
      `Filter change requested in ${instanceId}`,
      {
        oldFilters: filters,
        newFilters
      }
    );
    setFilters(newFilters);
  }, [filters, instanceId]);

  const selectedCount = useMemo(() => {
    const count = Object.values(selectedChannels).filter(Boolean).length;
    logger.debug(
      LOG_CONTEXTS.CHANNELS, 
      `Selected count calculated in ${instanceId}: ${count}`, 
      { selectedChannels }
    );
    return count;
  }, [selectedChannels, instanceId]);

  const selectedIds = useMemo(() => {
    const ids = Object.entries(selectedChannels)
      .filter(([_, selected]) => selected)
      .map(([id]) => parseInt(id));
    
    logger.debug(
      LOG_CONTEXTS.CHANNELS, 
      `Selected IDs calculated in ${instanceId}: ${ids.join(', ')}`, 
      { ids }
    );
    
    return ids;
  }, [selectedChannels, instanceId]);

  // Wrap the actions with read-only checks
  const handleAction = useCallback((action: () => void, actionName: string) => {
    logger.debug(
      LOG_CONTEXTS.CHANNELS, 
      `Action requested in ${instanceId}: ${actionName}`, 
      { isReadOnly }
    );
    
    if (isReadOnly) {
      logger.info(
        LOG_CONTEXTS.CHANNELS, 
        `Action blocked (read-only) in ${instanceId}: ${actionName}`
      );
      
      toast.error("This is a published version. Create a new version to make changes.");
      return;
    }
    
    logger.debug(
      LOG_CONTEXTS.CHANNELS, 
      `Executing action in ${instanceId}: ${actionName}`
    );
    
    action();
  }, [isReadOnly, instanceId]);

  const wrappedOpenCreateModal = useCallback(() => {
    handleAction(() => {
      // Pass regionId to the context when opening create modal
      originalOpenCreateModal({
        regionId
      });
    }, 'create');
  }, [handleAction, originalOpenCreateModal, regionId]);

  // Log component render
  logger.trackRender(`${instanceId} (providerId: ${providerId}, versionId: ${versionId}, regionId: ${regionId})`);

  // Process channel data
  const processChannelData = (data: ChannelNumberRow[]): Channel[] => {
    const queryRequestId = logger.createRequestId();
    // Group by channel and transform data
    const channelMap = data.reduce<Record<number, Channel>>((acc, row) => {
      const channel = row.channel;
      if (channel.provider_id === providerId && !acc[channel.id]) {
        // Initialize the channel with proper null values instead of undefined
        acc[channel.id] = {
          id: channel.id,
          name: channel.name,
          description: channel.description || null,
          price: channel.price || null,
          category_id: channel.category_id,
          provider_id: channel.provider_id,
          plan_id: null, // Add missing required field
          version_id: channel.version_id,
          category: channel.category,
          categories: channel.categories || [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          // Transform plans structure to match the Channel interface requirements
          plans: channel.plans.map(planItem => {
            if (planItem && planItem.plan && typeof planItem.plan === 'object') {
              return {
                id: planItem.plan.id,
                name: planItem.plan.name,
                color_index: planItem.plan.color_index
              };
            }
            // Fallback for unexpected format
            return {
              id: 0, 
              name: '',
              color_index: 0
            };
          }),
          numbers: [],
          is_local: !!channel.is_local, // Ensure boolean conversion
          is_duplicate: !!channel.is_duplicate, // Ensure boolean conversion
        };
      }
      if (acc[channel.id]) {
        // Add the number to the channel
        acc[channel.id].numbers.push({
          id: row.id,
          channel_id: channel.id,
          type_id: row.type.id,
          number: row.number,
          is_sequence: !!row.is_sequence, // Ensure boolean conversion
          is_default: false,
          type: {
            id: row.type.id,
            name: row.type.name,
            display_order: row.type.display_order
          }
        });
      }
      return acc;
    }, {});

    const channels = Object.values(channelMap);
    
    logger.debug(
      LOG_CONTEXTS.QUERY,
      `Processed ${data.length} rows into ${channels.length} channels in ${instanceId}`,
      { rowCount: data.length, channelCount: channels.length },
      queryRequestId
    );
    
    return channels;
  };

  // Handle query errors
  const handleQueryError = (error: any) => {
    const queryRequestId = logger.createRequestId();
    logger.error(
      LOG_CONTEXTS.QUERY, 
      `Query error in ${instanceId}`, 
      { error },
      queryRequestId
    );
    
    // Show user-friendly toast notification for different types of errors
    if (error?.code === 'PGRST100') {
      // This is a PostgREST syntax error
      toast.error("There was a problem with the search syntax. We've logged this issue. Please try a simpler search term.");
      
      // Log detailed information for debugging
      logger.error(
        LOG_CONTEXTS.QUERY,
        `PostgREST syntax error in ${instanceId}`,
        { 
          error,
          searchTerm: filters.search,
          details: error.details
        },
        queryRequestId
      );
    } else {
      toast.error("Failed to load channels. Please try again.");
    }
  };

  // Add a refresh timestamp state
  const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now());

  // Add the refresh function
  const handleRefresh = useCallback(() => {
    setRefreshTimestamp(Date.now());
    setLoading(true);
    logger.debug(
      LOG_CONTEXTS.CHANNELS, 
      `Manually refreshing data in ${instanceId}`,
      { timestamp: refreshTimestamp }
    );
  }, [instanceId, refreshTimestamp]);

  // Handle bulk plan assigner navigation
  const handleBulkPlanAssignerClick = useCallback(() => {
    const path = regionId 
      ? `/providers/${providerId}/regions/${regionId}/versions/${versionId}/bulk-plan-assigner`
      : `/providers/${providerId}/versions/${versionId}/bulk-plan-assigner`;
    navigate(path);
  }, [navigate, providerId, versionId, regionId]);

  if (error) {
    logger.error(
      LOG_CONTEXTS.CHANNELS, 
      `Error rendering ${instanceId}`, 
      { error }
    );
    return <ErrorDisplay error={error} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <h2 className="text-xl font-bold">Channels</h2>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleRefresh} 
            className={cn("mr-2", _loading ? "animate-spin" : "")}
            title="Refresh channels"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center space-x-3">
          {/* Add sorting controls - NEW */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="px-3 py-2">
                Sort By
                {sortField && 
                  ` ${sortField.charAt(0).toUpperCase() + sortField.slice(1)} (${sortDirection === 'asc' ? 'A-Z' : 'Z-A'})`
                }
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              <DropdownMenuItem onClick={() => toggleSort('name')} className="py-2">
                Channel Name {sortField === 'name' && (sortDirection === 'asc' ? '(A-Z)' : '(Z-A)')}
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => toggleSort('number')} className="py-2">
                Channel Number {sortField === 'number' && (sortDirection === 'asc' ? '(Low-High)' : '(High-Low)')}
                <ArrowUpDown className="ml-2 h-4 w-4" />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        
          {!isReadOnly && (
            <>
              <Button 
                variant="outline" 
                onClick={() => setIsTypeModalOpen(true)}
                className="gap-2"
              >
                <Settings className="h-4 w-4" />
                Channel Types
              </Button>
              <BulkImportButton providerId={providerId} versionId={versionId} />
              <Button
                variant="outline"
                onClick={handleBulkPlanAssignerClick}
                className="gap-2"
                disabled={!plans || plans.length === 0}
              >
                <Zap className="h-4 w-4" />
                Bulk Plan Assigner
              </Button>
              <Button 
                onClick={wrappedOpenCreateModal}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Channel
              </Button>
            </>
          )}
        </div>
      </div>

      <ChannelFilters
        providerId={providerId}
        versionId={versionId}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      {selectedCount > 0 && !isReadOnly && (
        <ChannelBulkActions
          providerId={providerId}
          versionId={versionId}
          selectedIds={selectedIds}
        />
      )}

      <div className="relative">
        {isFetching && (
          <div className="absolute inset-0 bg-background/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-md">
            <div className="flex flex-col items-center gap-3 bg-background/90 p-6 rounded-lg border shadow-lg">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm font-medium text-foreground">Updating channels...</p>
              <p className="text-xs text-muted-foreground">Please wait a moment</p>
            </div>
          </div>
        )}
        <ChannelTable
          providerId={providerId}
          versionId={versionId}
          channels={channels || []}
          isReadOnly={isReadOnly}
        />
      </div>

      {/* Total channels count */}
      <div className="flex justify-end items-center mt-4 px-2">
        <div className="text-sm text-muted-foreground">
          Total: <span className="font-semibold text-foreground">{channels?.length || 0}</span> {channels?.length === 1 ? 'channel' : 'channels'}
        </div>
      </div>

      {!isReadOnly && (
        <ChannelModals 
          providerId={providerId}
          versionId={versionId}
        />
      )}

      <ChannelTypeModal 
        providerId={providerId}
        versionId={versionId}
        open={isTypeModalOpen}
        onOpenChange={setIsTypeModalOpen}
      />

    </div>
  );
}